import React, { useState, useEffect } from 'react';
import { X, Users, DollarSign, CheckCircle2, Calendar, Trash2 } from 'lucide-react';
import { Store, StoreType, visionService, getDefaultPrice, getDefaultCosts } from '../../services/database/vision';
import { TeamManagement } from './TeamManagement';
import { toast } from 'sonner';
import '../../styles/custom-scroll.css';

interface StoreFormProps {
  onClose: () => void;
  onSuccess: () => void;
  store?: Store;
}

export const StoreForm: React.FC<StoreFormProps> = ({
  onClose,
  onSuccess,
  store
}) => {
  const [formData, setFormData] = useState({
    name: store?.name || '',
    plan: store?.plan || 'start' as StoreType,
    price: store?.price || getDefaultPrice('start'),
    costs: store?.costs || getDefaultCosts('start'),
    deadline: store?.deadline || new Date().toISOString()
  });

  const [selectedMembers, setSelectedMembers] = useState<string[]>(
    store?.team_members.map(m => m.id) || []
  );

  const [showTeamManagement, setShowTeamManagement] = useState(false);
  const [showFinancials, setShowFinancials] = useState(false);

  useEffect(() => {
    if (!store) {
      const defaultPrice = getDefaultPrice(formData.plan);
      const defaultCosts = getDefaultCosts(formData.plan);
      setFormData(prev => ({
        ...prev,
        price: defaultPrice,
        costs: defaultCosts
      }));
    }
  }, [formData.plan]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (store) {
        await visionService.updateStore(store.id, formData, selectedMembers);
        toast.success('Loja atualizada com sucesso!');
      } else {
        await visionService.addStore(formData, selectedMembers);
        toast.success('Loja adicionada com sucesso!');
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Erro ao salvar loja:', error);
      toast.error('Erro ao salvar loja');
    }
  };

  const calculateProfit = () => {
    const totalCost = Object.values(formData.costs).reduce((a, b) => a + b, 0);
    return formData.price - totalCost;
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="bg-[#111]/90 rounded-2xl p-8 w-full max-w-md border border-white/[0.08]">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">
            {store ? 'Editar Loja' : 'Nova Loja'}
          </h2>
          <div className="flex items-center gap-2">
            {store && (
              <>
                <button
                  onClick={() => {
                    const newDate = prompt('Nova data de entrega (YYYY-MM-DD):', store.deadline.split('T')[0]);
                    if (newDate) {
                      setFormData(prev => ({
                        ...prev,
                        deadline: new Date(newDate).toISOString()
                      }));
                    }
                  }}
                  className="p-2 hover:bg-white/[0.08] rounded-lg text-gray-400"
                  title="Editar data"
                >
                  <Calendar size={20} />
                </button>
                <button
                  onClick={async () => {
                    if (window.confirm('Tem certeza que deseja excluir esta loja?')) {
                      try {
                        await visionService.deleteStore(store.id);
                        toast.success('Loja excluída com sucesso');
                        onClose();
                      } catch (error) {
                        console.error('Error deleting store:', error);
                        toast.error('Erro ao excluir loja');
                      }
                    }
                  }}
                  className="p-2 hover:bg-white/[0.08] rounded-lg text-red-500"
                  title="Excluir loja"
                >
                  <Trash2 size={20} />
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/[0.08] rounded-lg text-gray-400"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto custom-scrollbar" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm text-gray-400 mb-2">
                Nome da Loja
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full h-12 bg-white/[0.08] rounded-xl px-4 text-white focus:outline-none focus:ring-2 focus:ring-white/[0.16]"
                required
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">
                Plano
              </label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: 'branding', label: 'Branding', color: 'bg-yellow-500' },
                  { value: 'premium', label: 'Premium', color: 'bg-blue-500' },
                  { value: 'start', label: 'Start', color: 'bg-emerald-500' },
                  { value: 'pontual', label: 'Pontual', color: 'bg-purple-500' }
                ].map(plan => (
                  <button
                    key={plan.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, plan: plan.value as StoreType }))}
                    className={`
                      h-12 rounded-xl px-4 flex items-center justify-between
                      ${formData.plan === plan.value
                        ? `${plan.color} text-white`
                        : 'bg-white/[0.08] hover:bg-white/[0.12] text-white'
                      }
                    `}
                  >
                    <span>{plan.label}</span>
                    {formData.plan === plan.value && (
                      <CheckCircle2 size={16} className="text-white" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">
                Responsáveis
              </label>
              <button
                type="button"
                onClick={() => setShowTeamManagement(true)}
                className="w-full h-12 bg-white/[0.08] hover:bg-white/[0.12] rounded-xl px-4 text-white flex items-center justify-between"
              >
                <span>
                  {selectedMembers.length
                    ? `${selectedMembers.length} membro${selectedMembers.length > 1 ? 's' : ''} selecionado${selectedMembers.length > 1 ? 's' : ''}`
                    : 'Selecionar membros'}
                </span>
                <Users size={20} />
              </button>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm text-gray-400">
                  Financeiro
                </label>
                <button
                  type="button"
                  onClick={() => setShowFinancials(!showFinancials)}
                  className="text-sm text-blue-500 hover:text-blue-400"
                >
                  {showFinancials ? 'Ocultar' : 'Mostrar'}
                </button>
              </div>
              
              {showFinancials && (
                <div className="space-y-4 p-4 bg-white/[0.03] rounded-xl">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">
                      Preço
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                        R$
                      </span>
                      <input
                        type="number"
                        value={formData.price}
                        onChange={e => setFormData(prev => ({ ...prev, price: Number(e.target.value) }))}
                        className="w-full h-12 bg-white/[0.08] rounded-xl pl-12 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-white/[0.16]"
                      />
                    </div>
                  </div>

                  {formData.plan === 'branding' && (
                    <>
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">
                          Custo SM
                        </label>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                            R$
                          </span>
                          <input
                            type="number"
                            value={formData.costs.sm}
                            onChange={e => setFormData(prev => ({ 
                              ...prev, 
                              costs: { ...prev.costs, sm: Number(e.target.value) }
                            }))}
                            className="w-full h-12 bg-white/[0.08] rounded-xl pl-12 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-white/[0.16]"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm text-gray-400 mb-2">
                          Custo Banners
                        </label>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                            R$
                          </span>
                          <input
                            type="number"
                            value={formData.costs.banners}
                            onChange={e => setFormData(prev => ({ 
                              ...prev, 
                              costs: { ...prev.costs, banners: Number(e.target.value) }
                            }))}
                            className="w-full h-12 bg-white/[0.08] rounded-xl pl-12 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-white/[0.16]"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm text-gray-400 mb-2">
                          Custo Motion
                        </label>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                            R$
                          </span>
                          <input
                            type="number"
                            value={formData.costs.motion}
                            onChange={e => setFormData(prev => ({ 
                              ...prev, 
                              costs: { ...prev.costs, motion: Number(e.target.value) }
                            }))}
                            className="w-full h-12 bg-white/[0.08] rounded-xl pl-12 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-white/[0.16]"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {(formData.plan === 'premium' || formData.plan === 'start') && (
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">
                        Custo Total
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                          R$
                        </span>
                        <input
                          type="number"
                          value={formData.costs.sm}
                          onChange={e => setFormData(prev => ({ 
                            ...prev, 
                            costs: { 
                              sm: Number(e.target.value),
                              banners: 0,
                              motion: 0
                            }
                          }))}
                          className="w-full h-12 bg-white/[0.08] rounded-xl pl-12 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-white/[0.16]"
                        />
                      </div>
                    </div>
                  )}

                  <div className="pt-4 border-t border-white/[0.08]">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Lucro</span>
                      <span className={`font-medium ${
                        calculateProfit() >= 0 ? 'text-emerald-500' : 'text-red-500'
                      }`}>
                        R$ {calculateProfit().toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 h-12 bg-white/[0.08] hover:bg-white/[0.12] rounded-xl text-white"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex-1 h-12 bg-white hover:bg-white/90 rounded-xl text-black font-medium"
              >
                {store ? 'Salvar' : 'Adicionar'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {showTeamManagement && (
        <TeamManagement
          onClose={() => setShowTeamManagement(false)}
          onSelect={(members) => {
            setSelectedMembers(members);
            setShowTeamManagement(false);
          }}
          selectedMembers={selectedMembers}
          selectionMode
        />
      )}
    </div>
  );
};
